import { useState } from "react";
import api from "../lib/api";
import { track } from "../lib/usageTracker";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

/**
 * #506 — the player's half of a COACH-initiated invite: Accept / Decline
 * for a `coach_links` row that is `invited` with `initiated_by: "coach"`.
 *
 * Rendered by Settings' CoachAccessCard in place of that row's usual
 * withdraw ✕ — the ✕ is for invites the PLAYER sent (withdrawing your own
 * ask); this pair is for invites ADDRESSED to the player (answering
 * someone else's). The caller decides which rows get which via
 * `initiated_by`, which the API serves normalised to exactly
 * 'coach' | 'player' (routes/coachLinks.ts `toApiLink`).
 *
 * A contained component rather than more inline JSX in Settings.jsx on
 * purpose: other lanes own heavy changes to that file, so this keeps
 * #506's footprint there to one import, one badge branch, and one mount
 * point. Same endpoints as CoachPortal's mirror-image pair for the
 * player-initiated direction: accept is `POST /coach-links/:id/accept`
 * (the route works out that THIS caller is the accepting party from the
 * link's direction), decline is `DELETE /coach-links/:id` (decline and
 * revoke are one verb server-side — see that route's DECLINE note).
 *
 * Accepting is CONSENT to share (spec §5): only after it does the coach
 * see any cricket data, which is why the button says "Accept" next to a
 * card whose copy already explains what acceptance shares.
 */
export default function CoachAccessInviteActions({ link, onDone }) {
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/coach-links/${link.id}/accept`);
      track("coach.invite_accepted");
      toast.success("Coach connected — they can now see your cricket data");
      onDone?.();
    } catch {
      toast.error("Could not accept the invite");
    }
    setBusy(false);
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.delete(`/coach-links/${link.id}`);
      toast.success("Invite declined — nothing was shared");
      onDone?.();
    } catch {
      toast.error("Could not decline the invite");
    }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-2 shrink-0" data-testid={`settings-coach-invite-actions-${link.id}`}>
      <Button
        size="sm"
        onClick={accept}
        disabled={busy}
        className="h-11 sm:h-9 px-3"
        style={{ background: "#16A34A" }}
        data-testid={`settings-coach-invite-accept-${link.id}`}
      >
        <Check size={14} className="mr-1" /> Accept
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={decline}
        disabled={busy}
        className="h-11 sm:h-9 px-3"
        data-testid={`settings-coach-invite-decline-${link.id}`}
      >
        <X size={14} className="mr-1" /> Decline
      </Button>
    </div>
  );
}
